import { Injectable } from '@angular/core';
import { BaseNotifier } from '../../../../shared/notifiers/base-notifier';
import { Ref } from '../../../../shared/providers/ref';
import { ShareState } from './share-state';
import { MapProvider } from '../map/map-provider';
import { LayerProvider } from '../../shared/layer/layer-provider';

@Injectable()
export class ShareNotifier extends BaseNotifier<ShareState> {
  private static readonly INITIAL: ShareState = {
    includeExtent: true,
    includeLayers: false,
    includeZoom: true,
    generatedUrl: null,
    generating: false,
    copied: false,
  };

  constructor(ref: Ref) {
    super(ref, ShareNotifier.INITIAL);
  }

  /** Activa/desactiva la opción de incluir extensión geográfica */
  toggleExtent(): void {
    this.updateState(s => ({ ...s, includeExtent: !s.includeExtent, generatedUrl: null, copied: false }));
  }

  /** Activa/desactiva la opción de incluir capas visibles */
  toggleLayers(): void {
    this.updateState(s => ({ ...s, includeLayers: !s.includeLayers, generatedUrl: null, copied: false }));
  }

  /** Activa/desactiva la opción de incluir zoom */
  toggleZoom(): void {
    this.updateState(s => ({ ...s, includeZoom: !s.includeZoom, generatedUrl: null, copied: false }));
  }

  /** Genera el enlace compartible con el estado actual del mapa */
  async generateLink(
    mapProvider: MapProvider,
    layerProvider: LayerProvider
  ): Promise<void> {
    this.updateState(s => ({ ...s, generating: true, generatedUrl: null, copied: false }));

    const state = this.snapshot();
    const mapState = mapProvider.state();
    const view = mapState.view;

    // Siempre construir desde origin + ruta fija para evitar duplicados de segmento
    const baseUrl = `${window.location.origin}/`;
    const params = new URLSearchParams();

    if (view) {
      if (state.includeZoom) {
        params.set('zoom', String(Math.round(view.zoom)));
      }

      if (state.includeExtent) {
        const ext = view.extent;
        if (ext) {
          params.set('xmin', ext.xmin.toFixed(6));
          params.set('ymin', ext.ymin.toFixed(6));
          params.set('xmax', ext.xmax.toFixed(6));
          params.set('ymax', ext.ymax.toFixed(6));
          params.set('wkid', String(ext.spatialReference?.wkid ?? 102100));
        }
      }

      if (state.includeLayers) {
        const layerState = layerProvider.state();
        const visibleIds: string[] = [];
        for (const cat of layerState.layers) {
          if (cat.layers) {
            for (const layer of cat.layers) {
              if (cat.visible && layer.visible) {
                visibleIds.push(layer.id);
              }
            }
          }
        }
        if (visibleIds.length > 0) params.set('layers', visibleIds.join(','));
      }
    }

    const generatedUrl = `${baseUrl}?${params.toString()}`;

    // Pequeño delay para UX
    await new Promise<void>(resolve => setTimeout(resolve, 400));

    this.updateState(s => ({ ...s, generating: false, generatedUrl }));
  }

  /** Copia la URL generada al portapapeles */
  async copyToClipboard(): Promise<void> {
    const url = this.snapshot().generatedUrl;
    if (!url) return;

    try {
      await navigator.clipboard.writeText(url);
      this.updateState(s => ({ ...s, copied: true }));
      // Resetear el estado de copiado después de 2.5s
      setTimeout(() => this.updateState(s => ({ ...s, copied: false })), 2500);
    } catch {
      // Fallback para navegadores sin soporte
      const el = document.createElement('textarea');
      el.value = url;
      el.style.position = 'fixed';
      el.style.opacity = '0';
      document.body.appendChild(el);
      el.focus();
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      this.updateState(s => ({ ...s, copied: true }));
      setTimeout(() => this.updateState(s => ({ ...s, copied: false })), 2500);
    }
  }

  /** Limpia el enlace generado */
  reset(): void {
    this.updateState(s => ({ ...s, generatedUrl: null, copied: false }));
  }
}
